import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { Button } from "../components/ui/Button";
import { Card } from "../components/ui/Card";
import { Input } from "../components/ui/Input";
import { SegmentedControl } from "../components/ui/SegmentedControl";
import { Alert } from "../components/ui/Alert";
import { ThemeSwitcher } from "../components/theme/ThemeSwitcher";
import { useAuth } from "../components/auth/AuthProvider";
import { AuthBrandingHeader } from "../components/auth/AuthBrandingHeader";
import { toast } from "../lib/toast";
import { trackEvent } from "../lib/auditCollector";
import { getPasskeySupport, passkeyLogin, type PasskeySupport } from "../lib/webauthn";
import { usePublicConfig } from "../hooks/usePublicConfig";
import { useBrandingSettings } from "../lib/settings/useBrandingSettings";
import { consumeStaleReloadFlag } from "../lib/versionCheck";

export const LoginPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { refresh, appConfig } = useAuth();
  const { data: publicConfig } = usePublicConfig();
  const branding = useBrandingSettings();
  const [mode, setMode] = useState("password");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [twofa, setTwofa] = useState("");
  const [recoveryCode, setRecoveryCode] = useState("");
  const [useRecovery, setUseRecovery] = useState(false);
  const [pendingToken, setPendingToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [passkeySupport, setPasskeySupport] = useState<PasskeySupport | null>(null);
  const [maintenanceMessage, setMaintenanceMessage] = useState<string | null>(null);
  const [activationMessage, setActivationMessage] = useState<string | null>(null);
  const [resetMessage, setResetMessage] = useState<string | null>(null);

  // Handle activation redirect from /auth/activate
  useEffect(() => {
    const activated = searchParams.get("activated");
    const reset = searchParams.get("reset");
    const next = new URLSearchParams(searchParams);
    let changed = false;

    if (activated === "success") {
      setActivationMessage("Your account has been activated. You can now sign in.");
      next.delete("activated");
      changed = true;
    } else if (activated === "error") {
      const reason = searchParams.get("reason");
      setError(reason ? decodeURIComponent(reason) : "Account activation failed.");
      next.delete("activated");
      next.delete("reason");
      changed = true;
    } else if (activated === "invalid") {
      setError("Invalid activation link.");
      next.delete("activated");
      changed = true;
    }

    if (reset === "success") {
      setResetMessage("Your password has been reset. Sign in with your new password.");
      next.delete("reset");
      changed = true;
    }

    if (changed) {
      setSearchParams(next, { replace: true });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const showTwoFaStep = useMemo(() => Boolean(pendingToken), [pendingToken]);
  const passkeySupported = useMemo(() => passkeySupport?.supported ?? false, [passkeySupport]);
  const registrationEnabled = useMemo(
    () => appConfig?.features.registrationEnabled ?? publicConfig?.registrationEnabled ?? true,
    [appConfig?.features.registrationEnabled, publicConfig?.registrationEnabled]
  );
  const authOptions = useMemo(
    () => (passkeySupported ? [{ id: "password", label: "Password" }, { id: "passkey", label: "Passkey" }] : [{ id: "password", label: "Password" }]),
    [passkeySupported]
  );

  useEffect(() => {
    let active = true;
    getPasskeySupport()
      .then((support) => {
        if (active) setPasskeySupport(support);
      })
      .catch(() => {
        if (active) {
          setPasskeySupport({
            supported: false,
            reason: "Passkeys are unavailable in this browser.",
            details: { secureContext: false, publicKeyCredential: false },
          });
        }
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;
    fetch("/api/system/maintenance", { headers: { Accept: "application/json" } })
      .then(async (res) => {
        const payload = await res.json().catch(() => null);
        if (!active) return;
        const maintenance = payload?.maintenance;
        if (maintenance?.enabled) {
          setMaintenanceMessage(maintenance.message ?? "System is in maintenance mode.");
        } else {
          setMaintenanceMessage(null);
        }
      })
      .catch(() => {
        if (active) setMaintenanceMessage(null);
      });
    return () => {
      active = false;
    };
  }, []);

  const handleLogin = async (event: React.FormEvent) => {
    event.preventDefault();
    trackEvent({ category: "ui", action: "auth.login-attempt", component: "LoginPage", details: { method: "password" } });
    setLoading(true);
    setError(null);

    try {
      const res = await fetch("/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password, remember })
      });
      const data = await res.json().catch(() => ({}));
      if (res.status === 423 || data?.error?.code === "MAINTENANCE_MODE") {
        setError(data?.error?.message ?? "System is in maintenance mode.");
        return;
      }
      if (data?.twoFactorRequired && data?.pendingToken) {
        setPendingToken(data.pendingToken);
        setNotice("Enter your verification code to finish signing in.");
        return;
      }
      if (!res.ok) {
        setError(data?.message ?? "Unable to sign in.");
        return;
      }
      await refresh();
      navigate(data?.redirectTo ?? "/profile", { replace: true });
    } catch {
      if (consumeStaleReloadFlag()) {
        setError("Dashboard was recently updated. Reloading with the latest version...");
        setTimeout(() => window.location.reload(), 1500);
      } else {
        setError("Network error. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePasskey = async () => {
    trackEvent({ category: "ui", action: "auth.login-attempt", component: "LoginPage", details: { method: "passkey" } });
    const support = passkeySupport;
    if (!support?.supported) {
      toast.error(support?.reason ?? "Passkeys are not available on this device.");
      return;
    }
    setLoading(true);
    try {
      const result = await passkeyLogin(username || undefined, remember);
      if (!result.ok) {
        toast.error(result.message ?? "Passkey login failed.");
        return;
      }
      await refresh();
      navigate(result.redirectTo ?? "/profile", { replace: true });
    } catch {
      toast.error("Passkey login is not available on this device.");
    } finally {
      setLoading(false);
    }
  };

  const handleTwoFa = async () => {
    trackEvent({ category: "ui", action: "auth.2fa-verify", component: "LoginPage", details: { method: useRecovery ? "recovery" : "totp" } });
    setNotice(null);
    setLoading(true);
    try {
      if (!pendingToken) {
        setNotice("Please sign in with your password first.");
        return;
      }
      const res = await fetch("/api/auth/2fa/verify", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          pendingToken,
          code: useRecovery ? undefined : twofa,
          recoveryCode: useRecovery ? recoveryCode : undefined
        })
      });
      const data = await res.json().catch(() => ({}));
      if (res.status === 423 || data?.error?.code === "MAINTENANCE_MODE") {
        setError(data?.error?.message ?? "System is in maintenance mode.");
        return;
      }
      if (!res.ok) {
        setError(data?.message ?? "Verification failed.");
        return;
      }
      await refresh();
      navigate(data?.redirectTo ?? "/profile", { replace: true });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="surface-grid flex min-h-screen items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        <AuthBrandingHeader
          title="Welcome back"
          subtitle="Sign in to your QuickBox Pro dashboard"
          authPages={publicConfig?.authPages}
          branding={branding.settings}
        />

        <Card className="glass-card">
          <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-lg font-semibold">Sign in</h2>
              <p className="text-sm text-muted-foreground">Choose an authentication method.</p>
            </div>
            <ThemeSwitcher compact />
          </div>

          {maintenanceMessage && (
            <Alert variant="warning" className="mb-4">
              {maintenanceMessage}
            </Alert>
          )}

          {activationMessage && (
            <Alert variant="success" className="mb-4">
              {activationMessage}
            </Alert>
          )}

          {resetMessage && (
            <Alert variant="success" className="mb-4">
              {resetMessage}
            </Alert>
          )}

          <SegmentedControl
            options={authOptions}
            value={mode}
            onChange={(value) => {
              setMode(value);
              if (value !== "password") {
                setPendingToken(null);
              }
            }}
            className="mb-5"
          />

          {passkeySupport && !passkeySupport.supported && (
            <div className="mb-4 rounded-lg border border-border/60 bg-muted/60 px-4 py-3 text-xs text-muted-foreground">
              {passkeySupport.reason ?? "Passkeys are not available in this browser."}
            </div>
          )}

          {error && (
            <div className="mb-4 rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive-foreground">
              {error}
            </div>
          )}

          {notice && (
            <div className="mb-4 rounded-lg border border-border/60 bg-muted/70 px-4 py-3 text-sm text-muted-foreground">
              {notice}
            </div>
          )}

          {mode === "password" && (
            <form className="space-y-4" onSubmit={handleLogin}>
              <div className="space-y-1">
                <label className="text-xs uppercase tracking-wide text-muted-foreground">Username or Email</label>
                <Input
                  placeholder="you@example.com"
                  value={username}
                  onChange={(event) => setUsername(event.target.value)}
                  autoComplete="username"
                  required
                />
              </div>
              <div className="space-y-1">
                <div className="flex items-center justify-between">
                  <label className="text-xs uppercase tracking-wide text-muted-foreground">Password</label>
                  <Link to="/forgot-password" className="text-xs text-muted-foreground hover:text-foreground">
                    Forgot password?
                  </Link>
                </div>
                <Input
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  autoComplete="current-password"
                  required
                />
              </div>
              {(publicConfig?.session?.rememberEnabled ?? true) && (
                <label className="flex items-center gap-2 text-sm text-muted-foreground">
                  <input
                    type="checkbox"
                    checked={remember}
                    onChange={(event) => setRemember(event.target.checked)}
                    className="h-4 w-4 rounded border-border/70 bg-input text-primary focus-visible:ring-2 focus-visible:ring-ring"
                  />
                  Remember me for {publicConfig?.session?.rememberDays ?? 7} days
                </label>
              )}
              <Button type="submit" size="lg" className="w-full" disabled={loading}>
                {loading ? "Signing in…" : "Sign in"}
              </Button>
              {registrationEnabled && (
                <p className="text-center text-xs text-muted-foreground">
                  Need an account? <Link to="/register">Create account</Link>
                </p>
              )}
            </form>
          )}

          {mode === "passkey" && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">Use a registered passkey on this device.</p>
              <Button
                type="button"
                size="lg"
                className="w-full"
                onClick={handlePasskey}
                disabled={!passkeySupported || loading}
              >
                Use Passkey
              </Button>
              <p className="text-xs text-muted-foreground">
                {passkeySupported
                  ? "Your browser will prompt for a platform or synced passkey."
                  : passkeySupport?.reason ?? "Passkeys are not supported in this browser."}
              </p>
            </div>
          )}

          {showTwoFaStep && (
            <div className="mt-6 space-y-4 rounded-lg border border-border/60 bg-muted/40 p-4">
              <div className="text-sm font-semibold">Two-factor verification</div>
              <div className="space-y-2">
                <label className="text-xs uppercase tracking-wide text-muted-foreground">
                  {useRecovery ? "Recovery code" : "Verification code"}
                </label>
                <Input
                  placeholder={useRecovery ? "ABCD-EFGH-IJKL" : "123 456"}
                  value={useRecovery ? recoveryCode : twofa}
                  onChange={(event) =>
                    useRecovery ? setRecoveryCode(event.target.value) : setTwofa(event.target.value)
                  }
                  autoComplete={useRecovery ? "one-time-code" : "one-time-code"}
                />
              </div>
              <label className="flex items-center gap-2 text-sm text-muted-foreground">
                <input
                  type="checkbox"
                  checked={useRecovery}
                  onChange={(event) => setUseRecovery(event.target.checked)}
                  className="h-4 w-4 rounded border-border/70 bg-input text-primary focus-visible:ring-2 focus-visible:ring-ring"
                />
                Use recovery code instead
              </label>
              <Button type="button" size="lg" className="w-full" onClick={handleTwoFa}>
                Verify
              </Button>
              <p className="text-xs text-muted-foreground">
                Enter the code from your authenticator app or a recovery code.
              </p>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};
